import { Document, DocumentSection } from '../../../application/domain/entities/Document';
import { DocumentDto, DocumentsResponseDto } from '../dto/res/DocumentResponseDto';
import CustomDocumentType from '../../../../section-2/application/domain/entities/CustomDocumentType';

export class DocumentMapper {
  /**
   * Converts API response to domain DocumentSection array
   * Groups documents by section and filters out empty sections
   * @param dto - The API response DTO
   * @param customDocumentTypes - Optional array of CustomDocumentType from Redux for display names
   */
  static toDomain(
    dto: DocumentsResponseDto,
    customDocumentTypes?: CustomDocumentType[]
  ): DocumentSection[] {
    const sections: DocumentSection[] = [];

    // Create a map for quick lookup of display names by value
    const displayNameMap = this.createDisplayNameMap(customDocumentTypes);

    // Map of section keys to their display labels
    const sectionConfig = [
      { key: 'section2' as const, label: 'Section 2' },
      { key: 'supplementB' as const, label: 'Supplement B' },
      { key: 'eVerify' as const, label: 'E-Verify' },
    ];

    // Process each configured section
    for (const config of sectionConfig) {
      const documents = dto[config.key];

      // Only add section if it has documents
      if (documents && documents.length > 0) {
        const mapped = documents
          .filter((doc) => doc.frontImage || doc.backImage)
          .map((doc) => this.documentToEntity(doc, displayNameMap));

        if (mapped.length > 0) {
          sections.push({ section: config.key, documents: mapped });
        }
      }
    }

    return sections;
  }

  /**
   * Creates a map for quick lookup of display names by document value/key
   */
  private static createDisplayNameMap(
    customDocumentTypes?: CustomDocumentType[]
  ): Map<string, string> {
    const map = new Map<string, string>();

    if (customDocumentTypes) {
      customDocumentTypes.forEach((doc) => {
        map.set(doc.value, doc.label);
      });
    }

    return map;
  }

  /**
   * A list's own document is named from the document types. A combination's other document and a
   * supporting document carry their own name, and a supporting document says it is one, since it
   * belongs to no list.
   */
  private static displayName(dto: DocumentDto, displayNameMap: Map<string, string>): string {
    const name = this.documentName(dto, displayNameMap);
    // Supplement B copies of the same document (yearly reverifications) are told apart by date.
    const signed = dto.supplementBSignatureDate;
    return signed ? `${name} (Supplement B signed ${formatSignedDate(signed)})` : name;
  }

  private static documentName(dto: DocumentDto, displayNameMap: Map<string, string>): string {
    if (dto.role && dto.role !== 'primary') {
      const name = dto.label || displayNameMap.get(dto.key) || dto.key;
      return dto.role === 'supporting' ? `Supporting document: ${name}` : name;
    }
    return displayNameMap.get(dto.key) || dto.key; // Fallback to key if not found
  }

  /**
   * Converts single document DTO to domain entity
   * Uses displayNameMap to lookup the display name for the document key
   */
  private static documentToEntity(dto: DocumentDto, displayNameMap: Map<string, string>): Document {
    return {
      key: dto.key,
      displayName: this.displayName(dto, displayNameMap),
      frontImage: dto.frontImage,
      backImage: dto.backImage,
    };
  }
}

/** MM/DD/YYYY, as the record shows dates; the raw value if it is not a date. */
function formatSignedDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getUTCMonth() + 1)}/${pad(date.getUTCDate())}/${date.getUTCFullYear()}`;
}
